/**
 * GOVOPS-1008C — Permit list/create/export via GovOps API with legacy CRA field mapping.
 */
import {
  createGovOpsClient,
  defaultGetAuthToken,
  getGovOpsClient,
  GovOpsAuthError,
} from './govopsClient';
import { getGovOpsOrgId, orgQueryParams } from './govopsConfig';
import { GovOpsEntitlementRequiredError, GOVOPS_ENTITLEMENT_MSG } from './govopsExceptions';
import { isGovOpsAuthError } from './govopsErrors';
import { parseCoord } from '../utils/mapCoordinates';
import { formatPermitAddress, normalizeAlarmLocation } from '../utils/permitFields';

export { GOVOPS_ENTITLEMENT_MSG, GovOpsEntitlementRequiredError, isGovOpsAuthError };

const GOVOPS_WORKFLOW_STATES = new Set([
  'intake',
  'under_review',
  'notice_sent',
  'violation_open',
  'payment_pending',
  'payment_received',
  'payment_overdue',
  'renewal_due',
  'renewal_notice_sent',
  'expired',
  'resolved',
  'closed',
  'appealed',
]);

function applyFeeFieldsToGovOpsBody(body, legacy) {
  if (!legacy) return body;
  const feeAmount =
    legacy.feeAmount !== undefined && legacy.feeAmount !== ''
      ? Number(legacy.feeAmount)
      : undefined;
  const balanceDue =
    legacy.balanceDue !== undefined && legacy.balanceDue !== ''
      ? Number(legacy.balanceDue)
      : undefined;
  if (feeAmount !== undefined && Number.isFinite(feeAmount)) body.feeAmount = feeAmount;
  if (legacy.feeCurrency) body.feeCurrency = String(legacy.feeCurrency).trim();
  if (balanceDue !== undefined && Number.isFinite(balanceDue)) body.balanceDue = balanceDue;
  if (
    legacy.paymentStatus === 'unpaid' ||
    legacy.paymentStatus === 'pending' ||
    legacy.paymentStatus === 'paid' ||
    legacy.paymentStatus === 'overdue'
  ) {
    body.paymentStatus = legacy.paymentStatus;
  }
  if (legacy.renewalDate) body.renewalDate = String(legacy.renewalDate).slice(0, 10);
  if (legacy.expirationDate) body.expirationDate = String(legacy.expirationDate).slice(0, 10);
  if (legacy.alarmCompanyId) body.alarmCompanyId = legacy.alarmCompanyId;
  const vendorName =
    legacy.alarmCompanyName ||
    (typeof legacy.alarmCompany === 'string' ? legacy.alarmCompany : legacy.alarmCompany?.name);
  if (vendorName) body.alarmCompanyName = vendorName;
  return body;
}

function parseProgram(program) {
  if (!program) return {};
  try {
    return JSON.parse(program);
  } catch (_) {
    return { description: program };
  }
}

function normalizeMetaCoordinates(meta) {
  if (!meta || !meta.coordinates) return meta;
  const lat = parseCoord(meta.coordinates.latitude ?? meta.coordinates.lat);
  const lng = parseCoord(meta.coordinates.longitude ?? meta.coordinates.lng);
  if (lat == null || lng == null) {
    const next = { ...meta };
    delete next.coordinates;
    return next;
  }
  return {
    ...meta,
    coordinates: { latitude: lat, longitude: lng },
  };
}

function buildProgramMeta(legacy) {
  const meta = { ...legacy };
  delete meta.status;
  delete meta.type;
  delete meta.permitType;
  delete meta.caseId;
  delete meta.feeAmount;
  delete meta.feeCurrency;
  delete meta.balanceDue;
  delete meta.paymentStatus;
  delete meta.renewalDate;
  delete meta.expirationDate;
  delete meta.alarmCompanyId;
  delete meta.alarmCompanyName;

  const alarmLocation = normalizeAlarmLocation(legacy);
  if (alarmLocation.street || alarmLocation.city) {
    meta.alarmLocation = {
      ...(typeof meta.alarmLocation === 'object' ? meta.alarmLocation : {}),
      ...alarmLocation,
    };
  }
  if (!meta.address) {
    const formatted = formatPermitAddress(meta.alarmLocation || alarmLocation);
    if (formatted) meta.address = formatted;
  }

  return normalizeMetaCoordinates(meta);
}

/** GovOps permit row → shape expected by AdminPermitTable / DashboardPage / PermitHistory */
export function mapGovOpsPermitToLegacy(row) {
  const meta = parseProgram(row.program);
  const createdMs = row.createdAt ? new Date(row.createdAt).getTime() : null;
  const ts = createdMs ? { seconds: Math.floor(createdMs / 1000) } : null;

  const lat = parseCoord(meta.coordinates?.latitude ?? meta.coordinates?.lat);
  const lng = parseCoord(meta.coordinates?.longitude ?? meta.coordinates?.lng);
  const coordinates =
    lat != null && lng != null ? { latitude: lat, longitude: lng } : null;

  const alarmLocation = meta.alarmLocation || {};

  return {
    id: row.id,
    type: row.permitType || meta.type || meta.propertyType || '',
    description: meta.description || row.city || '',
    coordinates,
    address: meta.address || formatPermitAddress(alarmLocation) || '',
    alarmLocation,
    applicantName: meta.applicantName,
    phoneNumber: meta.phoneNumber,
    contact: meta.contact,
    billingAddress: meta.billingAddress,
    alarmCompany: meta.alarmCompany,
    propertyType: meta.propertyType,
    checkNumber: meta.checkNumber,
    permitNumber: meta.permitNumber,
    date: meta.date || ts,
    createdAt: meta.createdAt || ts,
    status: row.status,
    orgId: row.orgId,
    caseId: row.caseId,
    feeAmount: row.feeAmount,
    feeCurrency: row.feeCurrency || 'USD',
    balanceDue: row.balanceDue,
    paymentStatus: row.paymentStatus,
    renewalDate: row.renewalDate,
    expirationDate: row.expirationDate,
    alarmCompanyId: row.alarmCompanyId,
    alarmCompanyName: row.alarmCompanyName || meta.alarmCompany,
  };
}

/** Legacy Firestore-shaped permit → GovOps create body */
export function mapLegacyPermitToGovOps(legacy) {
  const meta = buildProgramMeta(legacy);
  const loc = normalizeAlarmLocation(legacy);
  const city =
    loc.city ||
    legacy.alarmLocation?.city ||
    legacy.city ||
    (typeof legacy.description === 'string' ? legacy.description.slice(0, 120) : undefined);

  const body = {
    permitType: legacy.type || legacy.permitType || 'general',
    city: city || 'Unknown',
    program: JSON.stringify(meta),
    orgId: getGovOpsOrgId(),
    caseId: legacy.caseId,
    status: legacy.status && GOVOPS_WORKFLOW_STATES.has(legacy.status) ? legacy.status : 'intake',
  };
  return applyFeeFieldsToGovOpsBody(body, legacy);
}

/** PATCH body — omit status unless a valid GovOps workflow state is provided */
export function mapLegacyPermitPatchToGovOps(legacy) {
  const meta = buildProgramMeta(legacy);
  const patch = {};

  if (legacy.type || legacy.permitType) {
    patch.permitType = legacy.type || legacy.permitType;
  }
  const loc = normalizeAlarmLocation(legacy);
  const city =
    loc.city ||
    legacy.alarmLocation?.city ||
    legacy.city ||
    (typeof legacy.description === 'string' ? legacy.description.slice(0, 120) : undefined);
  if (city) patch.city = city;
  if (Object.keys(meta).length) patch.program = JSON.stringify(meta);

  const status = typeof legacy.status === 'string' ? legacy.status.trim() : '';
  if (status && GOVOPS_WORKFLOW_STATES.has(status)) {
    patch.status = status;
  }

  return applyFeeFieldsToGovOpsBody(patch, legacy);
}

export async function hasGovOpsAuthToken() {
  const token = await defaultGetAuthToken();
  return Boolean(token);
}

export function assertGovOpsEntitlement() {
  return hasGovOpsAuthToken().then((ok) => {
    if (!ok) throw new GovOpsEntitlementRequiredError();
  });
}

export async function fetchGovOpsHealth() {
  const client = getGovOpsClient();
  return client.health();
}

/** Signed-in entitlement probe (for status bar / banner; does not throw). */
export async function checkGovOpsEntitlement() {
  const token = await defaultGetAuthToken();
  if (!token) return { state: 'unsigned' };
  try {
    const client = getGovOpsClient();
    await client.listPermits(orgQueryParams());
    return { state: 'ok' };
  } catch (err) {
    if (isGovOpsAuthError(err)) {
      return { state: 'forbidden', message: err.message || GOVOPS_ENTITLEMENT_MSG };
    }
    return { state: 'error', message: err?.message || 'GovOps unavailable' };
  }
}

/** Replaces Firestore `getDocs(collection(db, 'permits'))` */
export async function fetchPermits() {
  await assertGovOpsEntitlement();
  const client = getGovOpsClient();
  const res = await client.listPermits(orgQueryParams());
  const rows = res?.permits || [];
  return rows.map(mapGovOpsPermitToLegacy);
}

/** Replaces Firestore `addDoc(collection(db, 'permits'), data)` */
export async function createPermitRecord(legacyPayload) {
  await assertGovOpsEntitlement();
  const client = getGovOpsClient();
  const body = mapLegacyPermitToGovOps(legacyPayload);
  const res = await client.createPermit(body);
  const row = res?.permit || res;
  return mapGovOpsPermitToLegacy(row);
}

/** Single permit by id */
export async function fetchPermitById(permitId) {
  await assertGovOpsEntitlement();
  const client = getGovOpsClient();
  const res = await client.getPermit(permitId);
  const row = res?.permit || null;
  return row ? mapGovOpsPermitToLegacy(row) : null;
}

/** Replaces Firestore `updateDoc(doc(db, 'permits', id), data)` */
export async function updatePermitRecord(permitId, legacyPatch) {
  await assertGovOpsEntitlement();
  const client = getGovOpsClient();
  const body = mapLegacyPermitPatchToGovOps(legacyPatch);
  const res = await client.updatePermit(permitId, body);
  const row = res?.permit || res;
  return mapGovOpsPermitToLegacy(row);
}

/** Server CSV export (requires entitlement) */
export async function exportPermitsCsv(extraQuery = {}) {
  await assertGovOpsEntitlement();
  const client = getGovOpsClient();
  return client.exportPermitsCsv({ ...orgQueryParams(), ...extraQuery });
}

export { getGovOpsClient, createGovOpsClient, GovOpsAuthError };
